
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

import NavHeader from "@/components/NavHeader.vue";

import draggable from "vuedraggable";
import vSelect from "vue-select";
import { required, minLength, requiredIf, between } from "vuelidate/lib/validators";
import { get, cloneDeep, clone } from "lodash";
import { validationMixin } from "vuelidate";

import { SiteTreeNode } from "@/store/eventqueue/types";

import { UserPermissions, FeaturesList } from "@/store/types";
import { Tour, TourSteps } from "@/store/tours/types";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";

const Areas = namespace("areas");
const Eventqueue = namespace("eventqueue");
const Tours = namespace("tours");
const SiteMonitor = namespace("siteMonitor");

const defaultTourDuration: number = 3;
@Component({
	components: {
		draggable,
		"v-select": vSelect,
		"nav-header": NavHeader,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"area-tree-select": AreaTreeSelect
	},
	mixins: [validationMixin],
	validations: {
		currentTour: {
			title: {
				required,
				minLength: minLength(3)
			},
			tourSteps: {
				required,
				$each: {
					duration: {
						required: requiredIf(function(tourStep) {
							return this.tourUsingDuration;
						}),
						// 3 seconds to 10mins if duration type tour, don't validate tour duration if confirmation.
						validateStepDuration(value): boolean {
							return this.tourUsingDuration
								? value >= defaultTourDuration && value <= 600
								: !this.tourUsingDuration
						}
					}
				}
			}
		}
	}
})
export default class TourSetupModal extends Vue {
	/** Store Getter/Setter */
	@Getter getPermissions: UserPermissions;
	@SiteMonitor.Action createAuditRecord: any;
	@Getter("getFeaturesList") featuresList: FeaturesList;

	@Mutation private setIsLoading: (loading: boolean) => void;

	@Tours.Action fetchAllTours: any;
	@Tours.Action removeTour: any;
	@Tours.Action addNewTour: any;
	@Tours.Action updateTour: any;
	@Tours.Action fetchDeviceList: any;
	@Tours.Action removeDevice: any;
	@Tours.Action searchDeviceList: any;
	@Tours.Getter getTour: any;
	@Tours.Getter getAllTours: any;
	@Tours.Getter getDeviceList: any;
	@Tours.Getter getSearchDevices: any;
	@Tours.Mutation addSearchDevices: any;
	@Tours.Mutation addDevice: any;

	@Areas.Action getAreaList: any;
	@Areas.Action updateArea: any;
	@Areas.Action deleteArea: any;
	@Areas.Action createArea: any;
	@Areas.Getter("getAreaList") areaList!: any[];

	@Prop({ default: 0, type: Number }) tourId;

	private tourUsingDuration: boolean = true;
	private toursTableData: any = [];

	private tourGroupId: number = 1;
	private searchableGroupId: number = 1;

	// Api search calls
	private deviceSearchTimer: any = null;
	private deviceSearchValue: string = "";

	private currentTour: Tour = {
		groupId: 0,
		title: "",
		tourId: 0,
		tourOrder: 0,
		tourSteps: [],
		functionality: null,
		tourStepsCount: 0
	};

	/** Filter through our current tour steps and if contains the same device, remove from the device list. */
	private get filteredDevices() {
		if (this.getSearchDevices.length > 0 && this.deviceSearchValue !== "") {
			return cloneDeep(
				this.getSearchDevices.filter(devices => {
					// Filter through the devices and only return the ones that hasn't been added already.
					return this.currentTour.tourSteps.find(tourStep => tourStep.objectID == devices.objectID)
						? false
						: true;
					// Map the device object with the title containing the area e.g. Area - CameraName
				})
			).map(device => this.getDeviceArea(device));
		} else {
			return cloneDeep(
				this.getDeviceList.filter(devices => {
					// Filter through the devices and only return the ones that hasn't been added already.
					return this.currentTour.tourSteps.find(tourStep => tourStep.objectID == devices.objectID)
						? false
						: true;
					// Map the device object with the title containing the area e.g. Area - CameraName
				})
			).map(device => {
				return this.getDeviceArea(device);
			});
		}
	}

	@Watch("tourId")
	private tourEditUpdated() {
		this.editTourDetails(this.tourId);
	}

	/** Method to fetch devices depending on the group you are currently on. */
	@Watch("searchableGroupId")
	private async updateDevicesList() {
		await this.fetchDeviceList(this.searchableGroupId);
	}

	private async mounted() {
		await this.fetchDeviceList(this.searchableGroupId);
		if (this.tourId > 0) {
			this.editTourDetails(this.tourId);
		} else {
			this.tourUsingDuration = true;
			this.currentTour = this.cloneTourTemplate();
		}

		this.setIsLoading(false);
	}

	private getDeviceArea(device: any) {
		let title = this.areaList.find(area => area.groupID == device.groupId);
		return {
			title: `${title ? title.title : ""} - ${device.title}`,
			groupId: device.groupId,
			objectID: device.objectID
		};
	}

	private cloneTourTemplate(): Tour {
		const tourTemplate: any = {
			groupId: 0,
			title: "",
			tourId: 0,
			tourOrder: 0,
			tourSteps: [],
			functionality: null,
			tourStepsCount: 0
		};
		// Deep cloned our template due to wanting it to be passed by VALUE not reference.
		return cloneDeep(tourTemplate);
	}

	private removeDeviceFromTour(device: any): void {
		this.currentTour.tourSteps.splice(
			this.currentTour.tourSteps.findIndex(x => x.objectID === device.objectID),
			1
		);
	}

	/** Loads the desired tour into the edit modal */
	public editTourDetails(tourId: number): void {
		this.currentTour = this.getTour(tourId);
		this.tourGroupId = this.currentTour.groupId;

		// steps based on confirmation is based on null, else duration is a number
		if (this.currentTour.tourSteps.length > 0) {
			this.currentTour.tourSteps[0].duration == 0
				? (this.tourUsingDuration = false)
				: (this.tourUsingDuration = true);
		}
	}

	/** Save new or update tour */
	public async Save() {
		this.currentTour.tourStepsCount = this.currentTour.tourSteps.length;
		this.currentTour.groupId = this.tourGroupId > 0 ? this.tourGroupId : 1;

		// new tour.
		if (this.currentTour.tourId == 0) {
			await this.addNewTour(this.currentTour);
		} else {
			this.currentTour.tourSteps.forEach(step => {
				if (this.tourUsingDuration) {
					step.duration = step.duration < 3 ? defaultTourDuration : step.duration;
				} else {
					step.duration = 0;
				}
			});

			await this.updateTour(this.currentTour);
		}

		this.tourUsingDuration = true;
		this.currentTour = this.cloneTourTemplate();
	}

	@Watch("$v.$invalid")
	private evaluateSetupValid() {
		this.$emit("setup-valid", this.$v.$invalid);
	}

	private addTourSetup(): void {
		this.currentTour = this.cloneTourTemplate();
	}

	private addDeviceToTour(device: any): void {
		let result = null;
		// Check if the user is searching or not then use the correct array.
		if (this.deviceSearchValue) {
			result = clone(this.getSearchDevices.find(x => x.objectID == device.objectID));
		} else {
			result = clone(this.getDeviceList.find(x => x.objectID == device.objectID));
		}

		let tourStep: TourSteps = {
			comment: "",
			objectID: result.objectID,
			duration: this.tourUsingDuration ? defaultTourDuration : 0,
			preset: 0,
			stepOrder: 0,
			tourId: this.currentTour.tourId,
			tourStepId: 0,
			groupId: result.groupId,
			title: result.title
		};

		this.currentTour.tourSteps.push(tourStep);
		this.addSearchDevices([]);
		if (this.tourUsingDuration && this.currentTour.tourSteps.length > 1) {
			this.setCurrentStepWithPreviousDuration();
		}
	}

	private addAllDevices() {
		this.getDeviceList.forEach((step: TourSteps, index, arr) => {
			const found = this.currentTour.tourSteps.findIndex(x => x.objectID == step.objectID);
			if (found == -1) {
				let tourStep: TourSteps = {
					comment: "",
					objectID: step.objectID,
					duration: this.tourUsingDuration ? defaultTourDuration : 0,
					preset: 0,
					stepOrder: 0,
					tourId: this.currentTour.tourId,
					tourStepId: 0,
					groupId: step.groupId,
					title: step.title
				};

				this.currentTour.tourSteps.push(tourStep);
			}
			if (this.tourUsingDuration && this.currentTour.tourSteps.length > 1) {
				this.setCurrentStepWithPreviousDuration();
			}
		});
	}

	private setCurrentStepWithPreviousDuration() {
		let currentDevice = this.currentTour.tourSteps[this.currentTour.tourSteps.length - 1];
		let previousDevice = this.currentTour.tourSteps[this.currentTour.tourSteps.length - 2];
		currentDevice.duration = previousDevice.duration;
	}

	private searchDevice(value: any, loading: any) {
		if (value == "") {
			this.addSearchDevices([]);
			return;
		}
		this.deviceSearchValue = value;
		clearTimeout(this.deviceSearchTimer);
		this.deviceSearchTimer = setTimeout(() => {
			this.searchDeviceList({ groupID: this.searchableGroupId, searchText: value });
		}, 1000);
	}
}
